import { get, isNil } from 'lodash'

import {
  setCartFromTab,
  setCartFromRecovery,
  addToCart,
  removeFromCart,
  updateCartItemQuantity,
  incrementCartItemQuantity,
  decrementCartItemQuantity,
  updateCartItemModifiers,
  updateCartItemNotes,
  clearCart,
  addNotesToCart,
  updateOpenPriceItem,
  addOrderNotesToCart,
  setSignatureDefaults,
  setTaxExemptData,
} from '../actions/cart'

export const defaultState = {
  lineItems: [],
  userNotes: null,
  orderNotes: null, // this will be just for local use. When sent to stadium the user notes and order notes will be merged.
  // it will be user notes first then order notes.
  selectedTipIndex: null,
  selectedTipAmount: null,
  shouldShowCharityFirst: false,
  signature: null,
  taxExemptData: {
    reason: '',
    taxExempt: false,
    taxExemptApproverUuid: '',
  },
}

const reducer = (state = defaultState, action = { payload: {} }) => {
  switch (action.type) {
    case setCartFromRecovery.type:
      const lineItems = get(action, 'payload.lineItems', [])
      return {
        ...state,
        lineItems,
      }

    case setCartFromTab.type: {
      const keysToKeep = ['menuItemUuid', 'price']
      const mappedKeys = {
        menuItemUuid: 'itemId',
        price: 'price',
      }
      const filterLineItemProperties = (lineItem) => {
        return Object.keys(lineItem)
          .filter((key) => keysToKeep.includes(key))
          .reduce((obj, key) => {
            obj[mappedKeys[key]] = lineItem[key]
            return obj
          }, {})
      }
      const lineItems = get(action, 'payload.lineItems', [])
        .filter((lineItem) => {
          return !isNil(lineItem.menuItemUuid) && !lineItem.itemSpecialType
        })
        .map((lineItem) => {
          return filterLineItemProperties(lineItem)
        })

      return {
        ...state,
        lineItems,
      }
    }

    case addToCart.type: {
      const newItem = {
        itemId: get(action, 'payload.itemId', ''),
        modifiers: get(action, 'payload.modifiers', []),
        quantity: get(action, 'payload.quantity', 1),
        isAlcohol: get(action, 'payload.isAlcohol', false),
        notes: get(action, 'payload.notes', null),
        openPriceInCents: get(action, 'payload.openPriceInCents'),
        originalPriceInCents: get(action, 'payload.originalPriceInCents'),
        openName: get(action, 'payload.openName'),
        managerId: get(action, 'payload.managerId'),
      }

      return {
        ...state,
        lineItems: [...state.lineItems, newItem],
      }
    }

    case removeFromCart.type: {
      const index = +get(action, 'payload.index', -1)
      let targetIndex = index
      if (index === -1) {
        targetIndex = state.lineItems.length - 1
      }

      const updatedLineItems = state.lineItems.filter((_, i) => i !== targetIndex)

      const taxExemptData = updatedLineItems.length
        ? state.taxExemptData
        : {
            reason: '',
            taxExempt: false,
            taxExemptApproverUuid: '',
          }

      return {
        ...state,
        lineItems: updatedLineItems,
        taxExemptData,
      }
    }

    case updateCartItemQuantity.type: {
      const index = +get(action, 'payload.index', -1)
      const quantity = get(action, 'payload.quantity')

      if (quantity === undefined || index < -1) return state

      const updatedLineItems = state.lineItems.map((item, i) => {
        const currentIndex = index === -1 ? state.lineItems.length - 1 : index
        if (i === currentIndex) {
          return { ...item, quantity: parseInt(quantity) }
        }
        return item
      })

      return {
        ...state,
        lineItems: updatedLineItems,
      }
    }

    case incrementCartItemQuantity.type: {
      const index = +get(action, 'payload.index', -1)
      const updatedLineItems = state.lineItems.map((item, i) => {
        const currentIndex = index === -1 ? state.lineItems.length - 1 : index
        if (i === currentIndex) {
          return { ...item, quantity: item.quantity + 1 }
        }
        return item
      })

      return {
        ...state,
        lineItems: updatedLineItems,
      }
    }

    case decrementCartItemQuantity.type: {
      const index = +get(action, 'payload.index', -1)
      const updatedLineItems = state.lineItems.map((item, i) => {
        const currentIndex = index === -1 ? state.lineItems.length - 1 : index
        if (i === currentIndex) {
          return { ...item, quantity: item.quantity - 1 }
        }
        return item
      })

      return {
        ...state,
        lineItems: updatedLineItems,
      }
    }

    case updateCartItemModifiers.type: {
      const index = +get(action, 'payload.index', -1)
      const modifiers = get(action, 'payload.modifiers', [])
      const updatedLineItems = state.lineItems.map((item, i) => {
        const currentIndex = index === -1 ? state.lineItems.length - 1 : index
        if (i === currentIndex) {
          return { ...item, modifiers: [...modifiers] }
        }
        return item
      })

      return {
        ...state,
        lineItems: updatedLineItems,
      }
    }

    case updateCartItemNotes.type: {
      const index = +get(action, 'payload.index', -1)
      const notes = get(action, 'payload.notes', [])
      const targetIndex = index === -1 ? state.lineItems.length - 1 : index

      const updatedLineItems = state.lineItems.map((item, i) => {
        if (i === targetIndex) {
          return { ...item, notes }
        }
        return item
      })

      return {
        ...state,
        lineItems: updatedLineItems,
      }
    }

    case setSignatureDefaults.type: {
      const selectedTipIndex = get(action, 'payload.selectedTipIndex', null)
      const selectedTipAmount = get(action, 'payload.selectedTipAmount', null)
      const shouldShowCharityFirst = get(action, 'payload.shouldShowCharityFirst', false)
      const signature = get(action, 'payload.signature', null)

      return {
        ...state,
        selectedTipIndex,
        selectedTipAmount,
        shouldShowCharityFirst,
        signature,
      }
    }

    case updateOpenPriceItem.type: {
      const index = +get(action, 'payload.index', -1)
      const managerId = get(action, 'payload.managerId', null)
      const updates = {
        openPriceInCents: get(action, 'payload.openPriceInCents', 0),
        originalPriceInCents: get(action, 'payload.originalPriceInCents', 0),
        openName: get(action, 'payload.openName', ''),
      }

      if (managerId) {
        updates.managerId = managerId
      }

      const updatedLineItems = state.lineItems.map((item, i) => {
        if (i === index) {
          return { ...item, ...updates }
        }
        return item
      })

      return {
        ...state,
        lineItems: updatedLineItems,
      }
    }

    case clearCart.type: {
      return {
        ...state,
        lineItems: [],
        userNotes: null,
        orderNotes: null,
        selectedTipIndex: null,
        selectedTipAmount: null,
        shouldShowCharityFirst: false,
        taxExemptData: {
          reason: '',
          taxExempt: false,
          taxExemptApproverUuid: '',
        },
      }
    }

    case addNotesToCart.type: {
      const updatedUserNotes = get(action, 'payload.userNotes', null)

      return {
        ...state,
        userNotes: updatedUserNotes,
      }
    }

    case addOrderNotesToCart.type: {
      const orderNotes = get(action, 'payload.orderNotes', null)

      return {
        ...state,
        orderNotes,
      }
    }

    case setTaxExemptData.type:
      const reason = action.payload.reason || ''
      const taxExempt = action.payload.taxExempt || false
      const taxExemptApproverUuid = action.payload.taxExemptApproverUuid || null

      return {
        ...state,
        taxExemptData: {
          ...state.taxExemptData,
          reason,
          taxExempt,
          taxExemptApproverUuid,
        },
      }

    default:
      return state
  }
}

export default reducer
